/*
  How the chart is arranged: the layout, and the custom pivot. Ported from the app's grouping modal
  (orgchart.html:1678-1706, :10516-10551).

  Both are LENS fields — per-user, no event, no history — and both were honoured by `buildHierarchy`
  and unreachable from the client, which is the same gap P4h-c closed for filters. Draft-with-Apply for
  the same reason it used there: there is nothing to invert, so the whole draft commits at once.

  Two deliberate divergences from the app:

   - The per-row dropdown excludes fields already used elsewhere in the draft. The app blocks a
     duplicate on Add (`:3345`) and permits one on change (`:3332`), which produces a pivot with the
     same level twice — every second level a single bucket containing everybody above it.
   - The empty-state copy says what Apply-with-no-levels actually does: return to the organisation's
     own structure (`:10538`). The app's own wording promises a flat chart with everybody under the
     root (`:3330`), which is not what the code does.

  The layout is refused rather than offered when the project has no mapped supervisor column: with
  none, every supervisor lookup returns '' and every person parents straight to the root — a flat fan
  of hundreds of cards that reads as "this company has no managers" rather than as "we do not have that
  data". `saveView` refuses the same thing at the command layer.
*/
import { useMemo, useState } from 'react';
import type { ViewGrouping } from '@integrate/contracts';
import { availableFields, type AvailableField, type SeparationState } from '@integrate/core';
import { Modal } from '../ui/Modal.js';
import { ICON, IconButton } from '../ui/Icon.js';

/** `viewGroupingSchema` caps a pivot at six levels; the UI says so rather than failing at the wire. */
const MAX_LEVELS = 6;

export function GroupingEditor({
  state,
  layout,
  grouping,
  targetView,
  onApply,
  onClose,
}: {
  state: SeparationState;
  layout: 'grouping' | 'reporting';
  grouping: ViewGrouping;
  /**
   * The blueprint is on, so the layout is not the caller's to choose.
   *
   * `buildHierarchy` returns the reporting tree before it ever reads `targetView`, so the two together
   * silently draw the reporting line with the Target-view button reading pressed. Disabled and said
   * out loud rather than resolved behind the user's back.
   */
  targetView: boolean;
  onApply(next: { viewLayout: 'grouping' | 'reporting'; viewGrouping: ViewGrouping }): void;
  onClose(): void;
}): JSX.Element {
  const fields = useMemo(() => availableFields(state), [state]);
  const byId = useMemo(() => new Map(fields.map((f) => [f.id, f])), [fields]);

  const [draftLayout, setDraftLayout] = useState(layout);
  const [draft, setDraft] = useState<ViewGrouping>(() => grouping.map((g) => ({ ...g })));
  const [adding, setAdding] = useState('');

  const hasSupervisor = !!state.columnMap.supervisorId;
  const layoutLocked = targetView || !hasSupervisor;

  const used = new Set(draft.map((g) => g.fieldId));
  const addable = fields.filter((f) => !used.has(f.id));

  const move = (index: number, by: number): void =>
    setDraft((prev) => {
      const next = [...prev];
      const to = index + by;
      if (to < 0 || to >= next.length) return prev;
      [next[index], next[to]] = [next[to]!, next[index]!];
      return next;
    });

  const apply = (): void => {
    // A level whose field the catalogue no longer offers resolves to '' for everybody and would
    // collapse the whole organisation into one bucket — dropped rather than applied, like an empty
    // filter condition.
    onApply({
      viewLayout: layoutLocked ? layout : draftLayout,
      viewGrouping: draft.filter((g) => byId.has(g.fieldId)),
    });
    onClose();
  };

  return (
    <Modal
      testId="grouping-modal"
      title="Group by"
      onClose={onClose}
      footer={
        <>
          <button
            data-testid="grouping-reset"
            onClick={() => {
              setDraft([]);
              if (!layoutLocked) setDraftLayout('grouping');
            }}
          >
            Reset
          </button>
          <span className="spacer" />
          <button data-testid="grouping-cancel" onClick={onClose}>
            Cancel
          </button>
          <button className="primary" data-testid="grouping-apply" onClick={apply}>
            Apply
          </button>
        </>
      }
    >
      <h5 className="asm-grid-h" style={{ marginTop: 0 }}>
        Layout
      </h5>
      <div className="lens" role="group" aria-label="Layout">
        <button
          data-testid="grouping-layout-grouping"
          className={draftLayout === 'grouping' ? 'lens-on' : ''}
          aria-pressed={draftLayout === 'grouping'}
          disabled={layoutLocked}
          onClick={() => setDraftLayout('grouping')}
        >
          Org structure
        </button>
        <button
          data-testid="grouping-layout-reporting"
          className={draftLayout === 'reporting' ? 'lens-on' : ''}
          aria-pressed={draftLayout === 'reporting'}
          disabled={layoutLocked}
          onClick={() => setDraftLayout('reporting')}
        >
          Reporting line
        </button>
      </div>
      {!hasSupervisor && (
        <p className="muted asm-help" data-testid="grouping-layout-blocked">
          The reporting-line layout needs a mapped <strong>Supervisor ID</strong> column. Map one in
          the column mapping, then re-derive the roster from source — without it every person would
          hang directly off the root.
        </p>
      )}
      {targetView && hasSupervisor && (
        <p className="muted asm-help" data-testid="grouping-layout-blocked">
          The target blueprint draws its own structure, so the layout is fixed while it is on. Turn
          off Target view to change it.
        </p>
      )}

      <h5 className="asm-grid-h">Custom pivot</h5>
      <p className="muted asm-help" style={{ marginTop: 0 }}>
        Replace the organisation&rsquo;s own levels with fields of your choosing, for viewing only.
        Nothing is changed by this — head designations and restructuring are unavailable while a
        pivot is on, because a pivot group is not a real group.
      </p>

      {draft.length === 0 ? (
        <p className="muted" data-testid="grouping-empty">
          No pivot. The chart follows the organisation&rsquo;s own structure.
        </p>
      ) : (
        <ol className="pivot-list">
          {draft.map((level, i) => {
            const known = byId.get(level.fieldId);
            return (
              <li key={level.fieldId} data-testid={`grouping-level-${i}`}>
                {known ? (
                  <select
                    data-testid={`grouping-field-${i}`}
                    value={level.fieldId}
                    onChange={(e) => {
                      const field = byId.get(e.target.value);
                      if (!field) return;
                      setDraft((prev) =>
                        prev.map((g, j) =>
                          j === i ? { fieldId: field.id, label: field.label } : g,
                        ),
                      );
                    }}
                  >
                    {/* The current one plus anything not already used elsewhere in the draft. */}
                    {[known, ...addable].map((f: AvailableField) => (
                      <option key={f.id} value={f.id}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="broken-field" data-testid={`grouping-broken-${level.fieldId}`}>
                    {level.label || level.fieldId} — no longer in the data
                  </span>
                )}
                {/*
                  These two had NO accessible name — no `aria-label`, no `title`, and a bare `↑` as
                  their only content. A screen reader announced "up arrow" or nothing at all, on the
                  control that decides what the chart is grouped by. `IconButton` makes the name a
                  required prop, so the shape that produced this cannot be written again.
                */}
                <IconButton
                  icon={ICON.moveUp}
                  testId={`grouping-up-${i}`}
                  label={`Move ${level.label || level.fieldId} up`}
                  disabled={i === 0}
                  onClick={() => move(i, -1)}
                />
                <IconButton
                  icon={ICON.moveDown}
                  testId={`grouping-down-${i}`}
                  label={`Move ${level.label || level.fieldId} down`}
                  disabled={i === draft.length - 1}
                  onClick={() => move(i, 1)}
                />
                <button
                  data-testid={`grouping-remove-${i}`}
                  onClick={() => setDraft((prev) => prev.filter((_, j) => j !== i))}
                >
                  Remove
                </button>
              </li>
            );
          })}
        </ol>
      )}

      {draft.length < MAX_LEVELS ? (
        <div className="filter-add">
          <select
            data-testid="grouping-add-field"
            value={adding}
            onChange={(e) => setAdding(e.target.value)}
          >
            <option value="">Add a level…</option>
            {addable.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label}
              </option>
            ))}
          </select>
          <button
            data-testid="grouping-add"
            disabled={!adding}
            onClick={() => {
              const field = byId.get(adding);
              if (!field) return;
              setDraft((prev) => [...prev, { fieldId: field.id, label: field.label }]);
              setAdding('');
            }}
          >
            Add
          </button>
        </div>
      ) : (
        <p className="muted asm-help">Six levels is the maximum.</p>
      )}
    </Modal>
  );
}
